import Head from "next/head"
import { MantineProvider } from "@mantine/core"
import wmMantineTheme from "themes-lib/wmTheme.mantine"
import { useEffect } from "react"
import { ModelManager } from "@adobe/aem-spa-page-model-manager"
import { Provider } from "react-redux"
import { PersistGate } from "redux-persist/integration/react"
import { logRocketInit } from "utils-lib/logRocket"
// import { customConsoleError } from "utils-lib/console"
import CustomModelClient from "@utils/CustomModelClient/CustomModelClient"
import suppressMappingErrors from "@utils/suppressMappingErrors/suppressMappingErrors"
import ThirdPartyScripts from "@components/ThirdPartyScripts/ThirdPartyScripts"
import CustomSpeedInsights from "@components/CustomSpeedInsights/CustomSpeedInsights"
import { ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import { wmTheme } from "themes-lib/wmTheme"
import { createEmotionSsrAdvancedApproach } from "tss-react/next/pagesDir"
import { TssCacheProvider } from "tss-react"
import "@styles/main.scss"
import "styles-lib/bootstrap.min.css"
import "styles-lib/main.scss"
import "styles-lib/globals.scss"
import "slick-carousel/slick/slick-theme.css"
import "slick-carousel/slick/slick.css"
import "@mantine/core/styles.css"
import { getWindow } from "utils-lib/getWindow"
import RouteChangeListener from "@components/RouteChangeListener/RouteChangeListener"
import LDAppWrapper from "component-lib/LaunchDarkly/LDClientWrapper"
import GoogleMapsProvider from "component-lib/GoogleMapsProvider"
import { store, persistor } from "@store/store"
import { setPersistor } from "utils-lib/persistorRef/persistorRef"

const APP_NAME = "wm-next"

const window = getWindow()

const {
  augmentDocumentWithEmotionCache: augmentDocumentWithEmotionCache_mui,
  withAppEmotionCache: withAppEmotionCache_mui,
} = createEmotionSsrAdvancedApproach({ key: "mui", prepend: true })

export { augmentDocumentWithEmotionCache_mui }

const {
  augmentDocumentWithEmotionCache: augmentDocumentWithEmotionCache_tss,
  withAppEmotionCache: withAppEmotionCache_tss,
} = createEmotionSsrAdvancedApproach({ key: "tss" }, TssCacheProvider)

export { augmentDocumentWithEmotionCache_tss }

if (
  process.env.NEXT_PUBLIC_NODE_ENV === "production" &&
  !process.env.NEXT_PUBLIC_IS_LOCAL_ENV
) {
  console.log = () => {}
  console.warn = () => {}
} else {
  // We are extending the default console.error functionality to filter out unwanted errors
  // console.nativeError = console.error
  // console.error = customConsoleError
}

// silence "Component not mapped for resourcetype: x" errors for non-existent components untl Sai and Pavan clean up on the AEM side
suppressMappingErrors()
setPersistor(persistor)

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST)
ModelManager.initializeAsync({
  modelClient,
})

function App({ Component, pageProps }) {
  const addSmoothScroll = () => {
    // adding a slight delay to not apply smooth scroll on intial load of a page, fixes some minor behavior if there is a hash in the url
    setTimeout(() => {
      window.document
        .querySelector("html")
        .setAttribute("class", "smooth-scroll")
    }, 150)
  }

  const removeSmoothScroll = () => {
    window.document
      .querySelector("html")
      .removeAttribute("class", "smooth-scroll")
  }

  // removing smooth scroll when user navigates via browser forward/back buttons to prevent unwanted scrolling
  const handleBrowserForwardBack = () => {
    removeSmoothScroll()
    addSmoothScroll()
  }

  const handleLogRocketLoad = () => {
    logRocketInit()
  }

  useEffect(() => {
    // eslint-disable-next-line no-extra-semi
    ;(async () => {
      const urls = await import("utils-lib/builds").then((mod) => mod.urls)
      const CognitoIdentityClient = await import(
        "@aws-sdk/client-cognito-identity"
      ).then((mod) => mod.CognitoIdentityClient)
      const fromCognitoIdentityPool = await import(
        "@aws-sdk/credential-provider-cognito-identity"
      ).then((mod) => mod.fromCognitoIdentityPool)

      // Initialize the Amazon Cognito credentials provider
      // eslint-disable-next-line no-new
      new CognitoIdentityClient({
        region: urls.chatbot.awsConfig.region,
        credentials: fromCognitoIdentityPool({
          client: new CognitoIdentityClient({
            region: urls.chatbot.awsConfig.region,
          }),
          IdentityPoolId: urls.chatbot.awsConfig.identityPoolId,
        }),
      })
    })()

    addSmoothScroll()

    window?.document.addEventListener(
      "logrocketload",
      handleLogRocketLoad,
      false,
    )

    window.addEventListener("popstate", handleBrowserForwardBack)

    return () => {
      window.removeEventListener("popstate", handleBrowserForwardBack)
      window?.document.removeEventListener("logrocketload", handleLogRocketLoad)
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderApp = () => (
    <GoogleMapsProvider
      apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY}
      region="US"
    >
      <ThemeProvider theme={wmTheme}>
        <MantineProvider theme={wmMantineTheme}>
          <Head>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, shrink-to-fit=no"
            />
          </Head>
          <ThirdPartyScripts />
          <CustomSpeedInsights />
          {/* Add any global application level wrappers here */}
          {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
          <CssBaseline />
          <Component {...pageProps} />
          <RouteChangeListener />
        </MantineProvider>
      </ThemeProvider>
    </GoogleMapsProvider>
  )

  return (
    <LDAppWrapper app={{ name: APP_NAME, version: window.__WMNEXT_VERSION__ }}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {/* hydration mismatch errors break dev server (something with Adobe's ResponsiveGrid and MUI, it's annoying and doesn't cause any issues with production builds so disabling SSR in dev for now) */}
          {process.env.NEXT_PUBLIC_NODE_ENV === "local"
            ? renderApp()
            : () => renderApp()}
        </PersistGate>
      </Provider>
    </LDAppWrapper>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// App.propTypes = {
//   Component: PropTypes.node.isRequired,
//   pageProps: PropTypes.object.isRequired,
// }

export default withAppEmotionCache_mui(withAppEmotionCache_tss(App))
